import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import {
  getEmailAccounts,
  sendEmailCampaign,
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  sendTestEmail
} from '../services/api';

interface CSVData {
  headers: string[];
  rows: Array<Record<string, string>>;
}

interface EmailTemplate {
  _id: string;
  name: string;
  subject: string;
  body: string;
}

const EmailCampaign: React.FC = () => {
  const [emailAccounts, setEmailAccounts] = useState<Array<{ email: string }>>([]);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [subjectTemplate, setSubjectTemplate] = useState<string>('');
  const [bodyTemplate, setBodyTemplate] = useState<string>('');
  const [testEmail, setTestEmail] = useState<string>('');
  const [sendingTest, setSendingTest] = useState<boolean>(false);
  
  // Templates state
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [savingTemplate, setSavingTemplate] = useState<boolean>(false);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);

  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [fileName, setFileName] = useState<string>('');
  
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const res = await getEmailTemplates();
      if (res.success && res.templates) {
        setTemplates(res.templates);
      }
    } catch (err) {
      console.error('Failed to load email templates', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const res = await getEmailAccounts();
        if (res.success && res.accounts) {
          setEmailAccounts(res.accounts);
          if (res.accounts.length > 0) {
            setSelectedEmail(res.accounts[0].email);
          }
        }
      } catch (err: any) {
        console.error('Failed to load email accounts', err);
        setMessage({
          type: 'error',
          text: 'Failed to load email accounts. Please ensure they are configured in the backend .env.'
        });
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccounts();
    fetchTemplates();
  }, []);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setSubjectTemplate('');
      setBodyTemplate('');
      return;
    }
    const found = templates.find(t => t._id === templateId);
    if (found) {
      setSubjectTemplate(found.subject);
      setBodyTemplate(found.body);
    }
  };

  const handleSaveTemplateAsNew = async () => {
    if (!newTemplateName.trim()) {
      setMessage({ type: 'error', text: 'Template name cannot be empty.' });
      return;
    }
    if (!subjectTemplate.trim() || !bodyTemplate.trim()) {
      setMessage({ type: 'error', text: 'Subject and Body cannot be empty to save a template.' });
      return;
    }

    try {
      setSavingTemplate(true);
      setMessage(null);
      const res = await createEmailTemplate({
        name: newTemplateName.trim(),
        subject: subjectTemplate,
        body: bodyTemplate
      });

      if (res.success && res.template) {
        setTemplates(prev => [res.template, ...prev]);
        setSelectedTemplateId(res.template._id);
        setNewTemplateName('');
        setSaveDialogOpen(false);
        setMessage({ type: 'success', text: `Template "${res.template.name}" saved successfully!` });
      }
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to save template.'
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplateId) return;
    const found = templates.find(t => t._id === selectedTemplateId);
    if (!found) return;

    try {
      setSavingTemplate(true);
      setMessage(null);
      const res = await updateEmailTemplate(selectedTemplateId, {
        name: found.name,
        subject: subjectTemplate,
        body: bodyTemplate
      });

      if (res.success && res.template) {
        setTemplates(prev => prev.map(t => t._id === selectedTemplateId ? res.template : t));
        setMessage({ type: 'success', text: `Template "${res.template.name}" updated successfully!` });
      }
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update template.'
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId) return;
    const found = templates.find(t => t._id === selectedTemplateId);
    if (!found) return;

    if (!window.confirm(`Are you sure you want to delete the template "${found.name}"?`)) {
      return;
    }

    try {
      setMessage(null);
      const res = await deleteEmailTemplate(selectedTemplateId);
      if (res.success) {
        setTemplates(prev => prev.filter(t => t._id !== selectedTemplateId));
        setSelectedTemplateId('');
        setSubjectTemplate('');
        setBodyTemplate('');
        setMessage({ type: 'success', text: 'Template deleted successfully.' });
      }
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to delete template.'
      });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (text) {
        const parsed = parseCSV(text);
        setCsvData(parsed);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): CSVData => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return { headers: [], rows: [] };
    
    // Parse headers
    const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
    const rows: Array<Record<string, string>> = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values: string[] = [];
      let insideQuote = false;
      let entry = '';
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuote = !insideQuote;
        } else if (char === ',' && !insideQuote) {
          values.push(entry.trim());
          entry = '';
        } else {
          entry += char;
        }
      }
      values.push(entry.trim());
      
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        let val = values[index] || '';
        val = val.replace(/^["']|["']$/g, '');
        row[header] = val;
      });
      rows.push(row);
    }
    
    return { headers, rows };
  };

  const handleSendCampaign = async () => {
    if (!selectedEmail) {
      setMessage({ type: 'error', text: 'Please select a sender email address.' });
      return;
    }
    if (!subjectTemplate.trim() || !bodyTemplate.trim()) {
      setMessage({ type: 'error', text: 'Subject and Email Body templates cannot be empty.' });
      return;
    }
    if (!csvData || csvData.rows.length === 0) {
      setMessage({ type: 'error', text: 'Please upload a CSV file with student records.' });
      return;
    }

    try {
      setSending(true);
      setMessage(null);
      
      const response = await sendEmailCampaign({
        senderEmail: selectedEmail,
        subjectTemplate,
        bodyTemplate,
        students: csvData.rows
      });

      if (response.success) {
        setMessage({
          type: 'success',
          text: response.message || `Campaign successfully started! Sending to ${csvData.rows.length} students.`
        });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to start email campaign.' });
      }
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'An error occurred while launching the campaign.'
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!selectedEmail) {
      setMessage({ type: 'error', text: 'Please select a sender email address.' });
      return;
    }
    if (!subjectTemplate.trim() || !bodyTemplate.trim()) {
      setMessage({ type: 'error', text: 'Subject and Email Body templates cannot be empty.' });
      return;
    }
    if (!testEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter a test email address.' });
      return;
    }

    try {
      setSendingTest(true);
      setMessage(null);

      const response = await sendTestEmail({
        senderEmail: selectedEmail,
        subjectTemplate,
        bodyTemplate,
        testEmail: testEmail.trim()
      });

      if (response.success) {
        setMessage({
          type: 'success',
          text: response.message || `Test email successfully sent to ${testEmail}.`
        });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to send test email.' });
      }
    } catch (err: any) {
      console.error(err);
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'An error occurred while sending the test email.'
      });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        Student Email Campaign
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        Upload a list of students via CSV, choose a configured Hostinger sender email, customize your template, and send bulk emails.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Form: Configuration & Template */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="medium">
                1. Sender & Student List
              </Typography>
              <Divider sx={{ my: 1.5 }} />

              {loadingAccounts ? (
                <Box display="flex" alignItems="center" gap={2} my={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">Loading SMTP Accounts...</Typography>
                </Box>
              ) : (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="sender-email-label">Select Sender Email</InputLabel>
                  <Select
                    labelId="sender-email-label"
                    value={selectedEmail}
                    label="Select Sender Email"
                    onChange={(e) => setSelectedEmail(e.target.value)}
                  >
                    {emailAccounts.map((account) => (
                      <MenuItem key={account.email} value={account.email}>
                        {account.email}
                      </MenuItem>
                    ))}
                    {emailAccounts.length === 0 && (
                      <MenuItem value="" disabled>
                        No SMTP email accounts configured in backend .env
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              <Box
                sx={{
                  border: '2px dashed #1976d2',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'action.hover',
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="csv-file-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="csv-file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                  <UploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    {fileName ? fileName : 'Upload Students CSV'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to browse your computer (.csv file only)
                  </Typography>
                </label>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="medium">
                2. Email Template
              </Typography>
              <Divider sx={{ my: 1.5 }} />

              {/* Template selector & CRUD actions */}
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="template-select-label">Choose Saved Template</InputLabel>
                  <Select
                    labelId="template-select-label"
                    value={selectedTemplateId}
                    label="Choose Saved Template"
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>-- None (Custom Template) --</em>
                    </MenuItem>
                    {templates.map((t) => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setSaveDialogOpen(true)}
                  >
                    Save As New
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={!selectedTemplateId}
                    onClick={handleUpdateTemplate}
                  >
                    Update Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={!selectedTemplateId}
                    onClick={handleDeleteTemplate}
                  >
                    Delete Selected
                  </Button>
                </Stack>
              </Box>

              <TextField
                fullWidth
                label="Email Subject"
                placeholder="e.g. Welcome to Hunar Punjab, {{name}}!"
                value={subjectTemplate}
                onChange={(e) => setSubjectTemplate(e.target.value)}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                multiline
                rows={10}
                label="Email Body (HTML supported)"
                placeholder="e.g. <p>Dear {{name}},</p><p>Your application is approved. Roll number: {{rollNumber}}.</p>"
                value={bodyTemplate}
                onChange={(e) => setBodyTemplate(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Card variant="outlined" sx={{ bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.main' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle2" color="info.main" gutterBottom fontWeight="bold">
                    Placeholders Tip:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    You can inject any column header from your uploaded CSV file using double curly braces:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {csvData && csvData.headers.length > 0 ? (
                      csvData.headers.map((header) => (
                        <Chip key={header} label={`{{${header}}}`} size="small" color="primary" variant="outlined" />
                      ))
                    ) : (
                      <>
                        <Chip label="{{name}}" size="small" variant="outlined" />
                        <Chip label="{{email}}" size="small" variant="outlined" />
                        <Chip label="{{rollNumber}}" size="small" variant="outlined" />
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* Test Email Section */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="text.secondary">
                  Test Your Template:
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    size="small"
                    label="Test Email Address"
                    placeholder="e.g. test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    size="medium"
                    disabled={sendingTest}
                    startIcon={sendingTest ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleSendTestEmail}
                  >
                    {sendingTest ? 'Sending...' : 'Send Test'}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Grid>

        {/* Right Form: Preview & Confirm */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom fontWeight="medium">
              3. Recipient Preview & Send
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            {csvData ? (
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                  Parsed Students ({csvData.rows.length} Total)
                </Typography>
                
                <TableContainer sx={{ maxHeight: 400, border: '1px solid #ddd', borderRadius: 1, mb: 3 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {csvData.headers.map((header) => (
                          <TableCell key={header} sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvData.rows.slice(0, 10).map((row, index) => (
                        <TableRow key={index} hover>
                          {csvData.headers.map((header) => (
                            <TableCell key={header}>{row[header] || ''}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {csvData.rows.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                    Showing first 10 students of {csvData.rows.length} total.
                  </Typography>
                )}

                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Campaign Summary:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Sender Email:</strong> {selectedEmail}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Total Recipients:</strong> {csvData.rows.length} students
                  </Typography>

                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={sending}
                    startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleSendCampaign}
                  >
                    {sending ? 'Sending Campaign...' : 'Send Email Campaign'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ py: 10, flexGrow: 1 }}
              >
                <Typography variant="body1" color="text.secondary" align="center">
                  Upload a student list CSV on the left to see the recipient preview and trigger sending.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Email Template</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a unique name to save this subject and email body template.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            variant="outlined"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={savingTemplate}>
            Cancel
          </Button>
          <Button onClick={handleSaveTemplateAsNew} variant="contained" disabled={savingTemplate || !newTemplateName.trim()}>
            {savingTemplate ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailCampaign;
